import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowLeft,
  Briefcase,
  CalendarClock,
  Link2,
  Mail,
  ShieldOff,
  UserPlus,
  Users,
} from "lucide-react"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { AttachAgencyDialog } from "@/components/admin/attach-agency-dialog"
import { BrandDangerZoneCard } from "@/components/admin/brand-danger-zone-card"
import { BrandListActAsButton } from "@/components/admin/brand-list-act-as-button"
import { InviteFirstAdminDialog } from "@/components/admin/invite-first-admin-dialog"
import { PendingFirstAdminInviteCard } from "@/components/admin/pending-first-admin-invite-card"
import { BrandLiveBadge } from "@/components/brand-live-badge"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useOnline } from "@/hooks/use-online"
import AppLayout from "@/layouts/app-layout"
import { nameOrDash } from "@/lib/admin-placeholder"
import { adminBrandActiveAgencyAssignmentPath, adminBrandsPath } from "@/routes"
import type { BrandShowProps } from "@/types/admin"

/**
 * Admin/Brands/Show — single brand detail (OKS-15 AC5 + UX Spec §2 IA).
 *
 * Stacked Card sections per UX Spec — Profile, Team, Agency assignment,
 * Metadata. The Assignment card is the ONLY mutable region in v1; all
 * other sections are read-only.
 *
 * Test IDs (locked v6 contract): `admin-brand-detail` (page region with
 * `data-brand-id`), `admin-brand-agency-card` (Assignment card),
 * `admin-agency-attach-button`, `admin-agency-attach-dialog` (dialog
 * content from `<AttachAgencyDialog>`), `admin-agency-replace-warning`
 * (banner inside dialog when swapping), `admin-agency-detach-button`,
 * `admin-agency-detach-confirm` (destructive Dialog primary).
 */
export default function AdminBrandsShow({
  brand,
  team,
  current_agency,
  agency_history,
  crew_size,
  agencies_available,
  pending_first_admin_invite,
  can_invite_first_admin,
  reset_preview,
}: BrandShowProps) {
  const { t, i18n } = useTranslation()
  const online = useOnline()

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
  const dateTimeFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  })

  return (
    <AppLayout>
      <Head title={brand.name} />
      <div
        className="flex flex-col gap-6 p-6"
        data-testid="admin-brand-detail"
        data-brand-id={brand.id}
      >
        <header className="flex flex-col gap-2">
          <Link
            href={adminBrandsPath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("admin.brand_detail.back")}
          </Link>
          {/*
           * OKS-13 F7 backport: `BrandLiveBadge` rendered inline with
           * the title. No-op when `brand.live === true` so the silent
           * norm reads cleanly; chip only appears for pending-activation
           * brands. Same exported symbol as the agency-side surface;
           * QA contract spec asserts the import.
           */}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-foreground text-2xl font-semibold tracking-tight">
              {brand.name}
            </h1>
            <BrandLiveBadge live={brand.live} testId="admin-brand-live-badge" />
            {/*
             * OKS-504 (EMV T6) — discoverability affordance on the brand
             * detail. Reuses the OKS-312 `BrandListActAsButton` (Inertia
             * <Form> POST to `adminActAsBrandPath`) so an admin can drop
             * into the brand from its detail page, then reach EMV via the
             * brand-sidebar Analytics entry → EMV tab. FE-only reuse — no
             * backend change (KD-4). Landing directly on the EMV tab is
             * deferred (would need an act-as return-to param = backend
             * change; PRD OQ-5 PR override).
             */}
            <div className="ml-auto">
              <BrandListActAsButton
                brand={{ id: brand.id, name: brand.name }}
              />
            </div>
          </div>
        </header>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Profile */}
          <Card>
            <CardHeader>
              <CardTitle>{t("admin.brand_detail.cards.profile")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <ProfileRow
                label={t("admin.brand_detail.profile.slug")}
                value={brand.slug}
              />
              <ProfileRow
                label={t("admin.brand_detail.profile.locale")}
                value={brand.locale.toUpperCase()}
              />
              <ProfileRow
                label={t("admin.brand_detail.profile.created_at")}
                value={dateFmt.format(new Date(brand.created_at))}
              />
              {brand.created_by_agency && (
                <ProfileRow
                  label={t("admin.brand_detail.profile.created_by_agency")}
                  value={brand.created_by_agency.name}
                />
              )}
            </CardContent>
          </Card>

          {/* Team */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                {t("admin.brand_detail.cards.team")}{" "}
                <span className="text-muted-foreground ml-1 text-sm font-normal">
                  ({team.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {team.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t("admin.brand_detail.team_empty")}
                </p>
              ) : (
                <ul className="flex flex-col gap-3" role="list">
                  {team.map((member) => (
                    <li
                      key={member.id}
                      className="flex items-start gap-3"
                      data-testid="admin-brand-team-member"
                    >
                      <Users
                        className="text-muted-foreground mt-0.5 size-4 shrink-0"
                        aria-hidden="true"
                      />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-foreground truncate text-sm font-medium">
                          {member.name}
                        </span>
                        <span className="text-muted-foreground inline-flex items-center gap-1 truncate text-xs">
                          <Mail className="size-3" aria-hidden="true" />
                          {member.email}
                        </span>
                      </div>
                      <Badge variant="secondary" className="ml-auto shrink-0">
                        {t(`admin.brand_detail.team_role.${member.role}`, {
                          defaultValue: member.role,
                        })}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/*
           * OKS-12 Pending first-admin invite Card.
           *
           * Three render branches owned by the Card itself:
           *   - pending/expired invite → row + Resend + Revoke
           *   - orphan (no invite + empty team) → EmptyState CTA
           *   - team populated + no invite → Card returns null
           *
           * `lg:col-span-2` so the card spans the full grid width when
           * present — matches the AssignmentCard treatment below.
           *
           * `onInviteFirstAdmin` is currently a no-op TODO — the CTA
           * needs a backend path that invites a first admin to an
           * EXISTING tenant (not the create-new-tenant `path=brand`
           * service). Backend gap flagged; `canInvite` is gated by
           * `can_invite_first_admin` so the server can hide the CTA
           * until the path lands.
           */}
          <div className="lg:col-span-2">
            <PendingFirstAdminInviteCard
              tenantId={brand.id}
              tenantKind="brand"
              viewer="admin"
              invite={pending_first_admin_invite}
              teamSize={team.length}
              // Backend ships `path: "first_admin"` (commit f24bd6e).
              // `can_invite_first_admin` is server-driven; pass the
              // dialog into the slot only when the server allows it.
              // The dialog mounts its own button as the trigger.
              inviteAction={
                can_invite_first_admin ? (
                  <InviteFirstAdminDialog
                    tenant={{
                      kind: "brand",
                      id: brand.id,
                      name: brand.name,
                    }}
                    trigger={
                      <Button
                        type="button"
                        data-testid="admin-pending-first-admin-empty-cta"
                      >
                        <UserPlus className="size-4" aria-hidden="true" />
                        {t("admin.invites.card.empty.cta")}
                      </Button>
                    }
                  />
                ) : undefined
              }
            />
          </div>

          {/* Agency assignment — the only mutable region */}
          <AssignmentCard
            brand={brand}
            currentAgency={current_agency}
            agencyHistory={agency_history}
            agenciesAvailable={agencies_available}
            online={online}
            dateTimeFmt={dateTimeFmt}
          />

          {/* Metadata — crew size + system meta */}
          <Card>
            <CardHeader>
              <CardTitle>{t("admin.brand_detail.cards.metadata")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <ProfileRow
                icon={Users}
                label={t("admin.brands_list.column.crew_size")}
                value={crew_size.toLocaleString()}
              />
              <ProfileRow
                icon={CalendarClock}
                label={t("admin.brand_detail.profile.created_at")}
                value={dateTimeFmt.format(new Date(brand.created_at))}
              />
            </CardContent>
          </Card>

          {/*
           * OKS-514 — Danger zone (destructive "Reset acquisition"). Last
           * card, full-width, visually separated from the read-only detail.
           * `reset_preview` is a deferred prop fetched on dialog open.
           */}
          <BrandDangerZoneCard
            brandId={brand.id}
            brandName={brand.name}
            preview={reset_preview}
          />
        </div>
      </div>
    </AppLayout>
  )
}

interface ProfileRowProps {
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: "true" }>
  label: string
  value: string
}

function ProfileRow({ icon: Icon, label, value }: ProfileRowProps) {
  return (
    <div className="flex items-center gap-3">
      {Icon ? (
        <Icon
          className="text-muted-foreground size-4 shrink-0"
          aria-hidden="true"
        />
      ) : (
        <div className="size-4 shrink-0" aria-hidden="true" />
      )}
      <span className="text-muted-foreground w-32 shrink-0">{label}</span>
      <span className="text-foreground truncate font-medium">{value}</span>
    </div>
  )
}

interface AssignmentCardProps {
  brand: BrandShowProps["brand"]
  currentAgency: BrandShowProps["current_agency"]
  agencyHistory: BrandShowProps["agency_history"]
  agenciesAvailable: BrandShowProps["agencies_available"]
  online: boolean
  dateTimeFmt: Intl.DateTimeFormat
}

function AssignmentCard({
  brand,
  currentAgency,
  agencyHistory,
  agenciesAvailable,
  online,
  dateTimeFmt,
}: AssignmentCardProps) {
  const { t } = useTranslation()
  const isSwap = currentAgency !== null

  return (
    <Card data-testid="admin-brand-agency-card" className="lg:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="inline-flex items-center gap-2">
          <Briefcase
            className="text-muted-foreground size-4"
            aria-hidden="true"
          />
          {t("admin.brand_detail.cards.assignment")}
        </CardTitle>
        <div className="flex flex-shrink-0 gap-2">
          <AttachAgencyDialog
            brandId={brand.id}
            brandName={brand.name}
            // OKS-905 — coerce the (defensively nullable) agency name to the
            // "—" placeholder at this boundary so AttachAgencyDialog keeps its
            // `name: string` contract; the null is structurally unreachable
            // under eager_load (KD-2).
            currentAgency={
              currentAgency
                ? { id: currentAgency.id, name: nameOrDash(currentAgency.name) }
                : null
            }
            agenciesAvailable={agenciesAvailable}
            trigger={
              <Button
                type="button"
                variant={isSwap ? "outline" : "default"}
                size="sm"
                disabled={!online}
                aria-disabled={!online ? "true" : undefined}
                data-testid="admin-agency-attach-button"
              >
                {/* Literal-string `t()` per branch so
                    `inertia_i18n_health_spec`'s static scanner can see
                    both keys (ternary-inside-t hides them). */}
                {isSwap
                  ? t("admin.brand_detail.assignment.swap_button")
                  : t("admin.brand_detail.assignment.attach_button")}
              </Button>
            }
          />
          {currentAgency && (
            <DetachAgencyDialog
              brandId={brand.id}
              brandName={brand.name}
              agencyName={nameOrDash(currentAgency.name)}
              online={online}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 text-sm">
        {currentAgency ? (
          <div className="bg-muted/50 flex items-center gap-2 rounded-md border p-3">
            <Link2 className="size-4 shrink-0" aria-hidden="true" />
            <span className="font-medium">
              {nameOrDash(currentAgency.name)}
            </span>
            <Badge variant="secondary" className="ml-auto">
              {t("admin.brand_detail.assignment.history_active")}
            </Badge>
          </div>
        ) : (
          <div className="text-muted-foreground inline-flex items-center gap-2 text-sm">
            <ShieldOff className="size-4" aria-hidden="true" />
            {t("admin.brand_detail.assignment.none")}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {t("admin.brand_detail.assignment.history_title")}
          </h3>
          {agencyHistory.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t("admin.brand_detail.assignment.history_empty")}
            </p>
          ) : (
            <ul
              className="flex flex-col gap-2"
              role="list"
              data-testid="admin-brand-agency-history"
            >
              {agencyHistory.map((entry) => {
                const isActive = entry.ended_at === null
                return (
                  <li
                    key={`${entry.agency.id}-${entry.starts_at}`}
                    className="flex items-start gap-3 text-sm"
                  >
                    <span
                      aria-hidden="true"
                      className={
                        isActive
                          ? "bg-primary mt-1.5 size-2 shrink-0 rounded-full"
                          : "bg-muted-foreground/40 mt-1.5 size-2 shrink-0 rounded-full"
                      }
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="text-foreground font-medium">
                        {nameOrDash(entry.agency.name)}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {dateTimeFmt.format(new Date(entry.starts_at))}
                        {entry.ended_at && (
                          <>
                            {" → "}
                            {dateTimeFmt.format(new Date(entry.ended_at))}
                          </>
                        )}
                        {" · "}
                        {t(
                          "admin.brand_detail.assignment.history_assigned_by",
                          { actor: entry.assigned_by.name },
                        )}
                      </span>
                    </div>
                    {isActive && (
                      <Badge variant="secondary" className="ml-auto">
                        {t("admin.brand_detail.assignment.history_active")}
                      </Badge>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface DetachAgencyDialogProps {
  brandId: number
  brandName: string
  agencyName: string
  online: boolean
}

/**
 * Destructive Detach confirm — uses shadcn `Dialog` (NOT `AlertDialog`)
 * per UX Spec §5: Cancel autoFocused, primary `variant="destructive"`,
 * explicit action label "Detach agency".
 */
function DetachAgencyDialog({
  brandId,
  brandName,
  agencyName,
  online,
}: DetachAgencyDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = useCallback(() => {
    setSubmitting(true)
    router.delete(adminBrandActiveAgencyAssignmentPath(brandId), {
      preserveScroll: true,
      onFinish: () => {
        setSubmitting(false)
        setOpen(false)
      },
    })
  }, [brandId])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!online}
        aria-disabled={!online ? "true" : undefined}
        onClick={() => setOpen(true)}
        data-testid="admin-agency-detach-button"
      >
        {t("admin.brand_detail.assignment.detach_button")}
      </Button>
      <DialogContent
        className="sm:max-w-md"
        data-testid="admin-agency-detach-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t("admin.brand_detail.detach_dialog.title", {
              agency: agencyName,
            })}
          </DialogTitle>
          <DialogDescription>
            {t("admin.brand_detail.detach_dialog.body", {
              agency: agencyName,
              brand: brandName,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            autoFocus
            onClick={() => setOpen(false)}
          >
            {t("admin.brand_detail.detach_dialog.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={submitting || !online}
            aria-disabled={submitting || !online ? "true" : undefined}
            onClick={handleSubmit}
            data-testid="admin-agency-detach-confirm"
          >
            {t("admin.brand_detail.detach_dialog.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
